import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus } from 'lucide-react';
import { masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const TAB_DOCTYPE = { languages: 'language', countries: 'country', currencies: 'currency' };

const MasterData = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'languages';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [data, setData] = useState({
    languages: [],
    countries: [],
    currencies: [],
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [languages, countries, currencies] = await Promise.all([
        masterDataAPI.getLanguages(),
        masterDataAPI.getCountries(),
        masterDataAPI.getCurrencies(),
      ]);
      setData({
        languages: languages.data,
        countries: countries.data,
        currencies: currencies.data,
      });
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formDataObj = {};
    formData.forEach((value, key) => {
      formDataObj[key] = value;
    });

    try {
      const id = editingItem?.[getIdField()];
      switch (activeTab) {
        case 'languages':
          editingItem ? await masterDataAPI.updateLanguage(id, formDataObj) : await masterDataAPI.createLanguage(formDataObj);
          break;
        case 'countries':
          editingItem ? await masterDataAPI.updateCountry(id, formDataObj) : await masterDataAPI.createCountry(formDataObj);
          break;
        case 'currencies':
          editingItem ? await masterDataAPI.updateCurrency(id, formDataObj) : await masterDataAPI.createCurrency(formDataObj);
          break;
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      switch (activeTab) {
        case 'languages':
          await masterDataAPI.deleteLanguage(id);
          break;
        case 'countries':
          await masterDataAPI.deleteCountry(id);
          break;
        case 'currencies':
          await masterDataAPI.deleteCurrency(id);
          break;
        default:
          break;
      }
      loadData();
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };
  void handleDelete;


  const getIdField = () => {
    switch (activeTab) {
      case 'languages': return 'language_code';
      case 'countries': return 'country_code';
      case 'currencies': return 'currency_code';
      default: return 'id';
    }
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'languages':
        return (
          <React.Fragment>
            <div>
              <Label>Language Code</Label>
              <Input 
                name="language_code" 
                defaultValue={editingItem?.language_code} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., EN"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Language Name</Label>
              <Input name="language_name" defaultValue={editingItem?.language_name} required placeholder="e.g., English" />
            </div>
          </React.Fragment>
        );
      case 'countries':
        return (
          <React.Fragment>
            <div>
              <Label>Country Code</Label>
              <Input 
                name="country_code" 
                defaultValue={editingItem?.country_code} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., US"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Country Name</Label>
              <Input name="country_name" defaultValue={editingItem?.country_name} required placeholder="e.g., United States" />
            </div>
          </React.Fragment>
        );
      case 'currencies':
        return (
          <React.Fragment>
            <div>
              <Label>Currency Code</Label>
              <Input 
                name="currency_code" 
                defaultValue={editingItem?.currency_code} 
                required 
                disabled={!!editingItem} 
                placeholder="e.g., USD"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Currency Name</Label>
              <Input name="currency_name" defaultValue={editingItem?.currency_name} required placeholder="e.g., US Dollar" />
            </div>
          </React.Fragment>
        );
    }
  };

  const tabs = [
    { id: 'languages', label: 'Languages' },
    { id: 'countries', label: 'Countries' },
    { id: 'currencies', label: 'Currencies' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="master-data-page">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Master Data</h2>
          <p className="text-sm text-gray-500 mt-1">Manage system-wide master data</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setEditingItem(null);
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              {canWrite(TAB_DOCTYPE[activeTab]) && (
                <DialogTrigger asChild>
                  <Button onClick={() => setEditingItem(null)} data-testid="btn-add-new">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1" data-testid="btn-submit-form">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Code</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data[activeTab].length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  data[activeTab].map((item) => (
                    <tr key={item[getIdField()]} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{item[getIdField()]}</td>
                      <td className="py-3 px-4 text-sm">
                        {item.language_name || item.country_name || item.currency_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <RecordActions
                            doctype={TAB_DOCTYPE[activeTab]}
                            idForTestId={item[getIdField()]}
                            onView={() => setViewItem(item)}
                            onEdit={() => { setEditingItem(item); setIsDialogOpen(true); }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find((t) => t.id === activeTab)?.label} Details`}
        record={viewItem}
      />
    </Layout>
  );
};

export default MasterData;
